import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography
} from '@mui/material'
import { useTranslation } from 'hooks/useTranslation'
import ButtonLink from 'modules/button/components/ButtonLink'
import { useState } from 'react'
import { OAuthConsentBody } from 'services/api/iam/manager/interfaces/OAuthConsentRequest'
import { OAuthInteractionResponse } from 'services/api/iam/manager/interfaces/OAuthInteractionResponse'

type Props = {
  interaction: OAuthInteractionResponse
  busy: boolean
  onSubmit: (body: OAuthConsentBody) => void
  onCancel: () => void
  onSwitchUser: () => void
}
export default function OAuthConsent({
  interaction,
  busy,
  onSubmit,
  onCancel,
  onSwitchUser
}: Props) {
  const { t } = useTranslation()
  const [policy, setPolicy] = useState<'SELECTED' | 'ALL_ACCESSIBLE'>('SELECTED')
  const [selected, setSelected] = useState<string[]>([])
  const [initial, setInitial] = useState('')
  const selectedAccounts = interaction.accounts.filter(account => selected.includes(account.id))
  const eligible = policy === 'ALL_ACCESSIBLE' ? interaction.accounts : selectedAccounts
  const validInitial = eligible.some(account => account.id === initial)
    ? initial
    : eligible[0]?.id || ''
  const selectAccounts = (ids: string[]) => {
    setSelected(ids)
    setInitial(current => (ids.includes(current) ? current : ''))
  }

  return (
    <Stack gap="30px">
      <Stack gap="10px">
        <Typography variant="h5" component="h1" sx={{ overflowWrap: 'anywhere' }}>
          {t('Autorizar {{app}}', { app: interaction.client.name })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            'Este aplicativo poderá consultar e alterar dados nas contas autorizadas, conforme suas permissões atuais.'
          )}
        </Typography>
        <Stack gap="2px" marginTop="6px">
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {interaction.user?.name} ({interaction.user?.email})
          </Typography>
          <ButtonLink
            disabled={busy}
            onClick={onSwitchUser}
            sx={{ alignSelf: 'flex-start', color: 'text.secondary', fontSize: '12px' }}
          >
            {t('Entrar com outro usuário')}
          </ButtonLink>
        </Stack>
      </Stack>
      {!interaction.accounts.length ? (
        <Alert severity="info">{t('Você não possui contas disponíveis para autorizar.')}</Alert>
      ) : (
        <>
          <Stack gap="10px">
            <FormLabel id="oauth-accounts-label">{t('Contas autorizadas')}</FormLabel>
            <RadioGroup
              aria-labelledby="oauth-accounts-label"
              value={policy}
              onChange={(_, value) => {
                setPolicy(value as typeof policy)
                setInitial('')
              }}
            >
              {[
                { value: 'ALL_ACCESSIBLE', label: t('Todas as minhas contas, atuais e futuras') },
                { value: 'SELECTED', label: t('Escolher contas') }
              ].map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" disabled={busy} sx={{ paddingLeft: 0 }} />}
                  label={option.label}
                  sx={{ margin: 0 }}
                />
              ))}
            </RadioGroup>
            {policy === 'SELECTED' ? (
              <Stack
                sx={{
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  marginLeft: '9px',
                  paddingLeft: '12px'
                }}
              >
                <FormControlLabel
                  sx={{ margin: 0 }}
                  label={t('Selecionar todas as atuais')}
                  control={
                    <Checkbox
                      size="small"
                      disabled={busy}
                      checked={selectedAccounts.length === interaction.accounts.length}
                      indeterminate={
                        selectedAccounts.length > 0 &&
                        selectedAccounts.length < interaction.accounts.length
                      }
                      onChange={(_, checked) =>
                        selectAccounts(
                          checked ? interaction.accounts.map(account => account.id) : []
                        )
                      }
                    />
                  }
                />
                <Stack sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {interaction.accounts.map(account => (
                    <FormControlLabel
                      key={account.id}
                      sx={{
                        margin: 0,
                        '& .MuiFormControlLabel-label': { overflowWrap: 'anywhere' }
                      }}
                      label={account.name}
                      control={
                        <Checkbox
                          size="small"
                          disabled={busy}
                          checked={selected.includes(account.id)}
                          onChange={(_, checked) =>
                            selectAccounts(
                              checked
                                ? [...selected, account.id]
                                : selected.filter(id => id !== account.id)
                            )
                          }
                        />
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            ) : (
              <Alert severity="info">
                {t(
                  'Contas às quais você ganhar acesso no futuro também estarão autorizadas para este aplicativo.'
                )}
              </Alert>
            )}
          </Stack>
          <FormControl fullWidth>
            <FormLabel id="oauth-initial-account-label">{t('Conta inicial')}</FormLabel>
            <Select
              labelId="oauth-initial-account-label"
              value={validInitial}
              displayEmpty
              disabled={busy || !eligible.length}
              onChange={event => setInitial(event.target.value)}
              renderValue={value =>
                eligible.find(account => account.id === value)?.name || (
                  <Typography variant="body2" color="text.disabled">
                    {t('Selecione ao menos uma conta')}
                  </Typography>
                )
              }
            >
              {eligible.map(account => (
                <MenuItem key={account.id} value={account.id}>
                  {account.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
      <Stack gap="14px">
        {interaction.accounts.length ? (
          <LoadingButton
            size="large"
            loading={busy}
            variant="contained"
            disabled={busy || !validInitial}
            onClick={() =>
              onSubmit({
                approved: true,
                account_policy: policy,
                account_ids:
                  policy === 'SELECTED' ? selectedAccounts.map(account => account.id) : [],
                account_id: validInitial
              })
            }
          >
            {t('Autorizar acesso')}
          </LoadingButton>
        ) : null}
        <ButtonLink disabled={busy} onClick={onCancel} sx={{ alignSelf: 'center' }}>
          {t('Cancelar')}
        </ButtonLink>
      </Stack>
    </Stack>
  )
}
